// =============================================================================
// services/markets.ts — the ONE data layer for Markets + Coin Detail.
//
// ROOT CAUSE of the P0 sort bug (IMG_5848): the old client fetched
// GET /crypto/prices, whose backend (apps/api routes/missing-endpoints.ts
// fetchLivePrices) builds its array with Object.entries() over CoinGecko's
// /simple/price response — an object keyed by coin id, which arrives in
// ALPHABETICAL id order (aave, avalanche-2, binancecoin, bitcoin, …). The client
// then treated ARRAY INDEX as market-cap rank, so "Market Cap" ranked Aave #1,
// Avalanche #2, BNB #3 and buried BTC/ETH.
//
// FIX (client-side, twofold):
//   1. Primary source is GET /market/tokens (CoinGecko /coins/markets ordered
//      market_cap_desc, with real coin LOGOS + 1h/24h/7d change) — 50 coins,
//      not the 10-coin /crypto/prices set.
//   2. We NEVER trust array order: sortTokens() re-sorts by the actual metric
//      for the selected chip (marketCap desc default), so even the alphabetical
//      /crypto/prices fallback ranks correctly.
//
// Endpoints used (all public):
//   GET /market/tokens?limit=50            → { tokens:[{ id,symbol,name,image,
//        currentPrice, marketCap, marketCapRank, totalVolume,
//        priceChangePercentage1h/24h/7d, high24h, low24h, ... }] }
//   GET /crypto/prices?limit=50            → bare array { symbol,name,price,
//        change24h,volume24h,marketCap } (ALPHABETICAL — fallback only)
//   GET /crypto/market-overview            → { totalMarketCap, totalVolume24h,
//        btcDominance, ethDominance, ... }
//   GET /market/tokens/:symbol             → detail incl. description, links
//        { homepage,twitter,reddit,github }, marketData (+ sparkline7d)
//   GET /crypto/tokens/:symbol/history?tf= → [{ time, price }], tf ∈ 24h|7d|30d|1y
// =============================================================================
import { api } from './api';

// ---- Types -------------------------------------------------------------------

export interface MarketToken {
  id: string;
  /** Upper-cased ticker, e.g. "BTC". */
  symbol: string;
  name: string;
  /** Real coin logo URL when the source provides one (fixes the monogram P1). */
  image?: string;
  price: number;
  change1h?: number | null;
  change24h: number;
  change7d?: number | null;
  marketCap: number;
  volume24h: number;
  /** Provider market-cap rank when present. Display only — never used to sort. */
  rank?: number | null;
}

export interface MarketOverview {
  totalMarketCap?: number;
  totalVolume24h?: number;
  btcDominance?: number;
  ethDominance?: number;
}

export interface TokenDetail {
  id: string;
  symbol: string;
  name: string;
  image?: string;
  description?: string;
  categories?: string[];
  websiteUrl?: string | null;
  whitepaperUrl?: string | null;
  twitterUrl?: string | null;
  price?: number;
  change24h?: number | null;
  change7d?: number | null;
  marketCap?: number;
  volume24h?: number;
  rank?: number | null;
  high24h?: number;
  low24h?: number;
  circulatingSupply?: number;
  /** Real 7-day price series when CoinGecko provides it. */
  sparkline7d?: number[];
}

export type SortKey = 'Market Cap' | 'Gainers' | 'Losers' | 'Volume';
export const SORT_KEYS: SortKey[] = ['Market Cap', 'Gainers', 'Losers', 'Volume'];

// ---- Normalizers ---------------------------------------------------------------

// Accepts BOTH row shapes: /market/tokens (currentPrice/priceChangePercentage24h)
// and /crypto/prices (price/change24h). Defensive against snake_case strays.
export function normalizeToken(raw: any): MarketToken {
  const symbol = String(raw?.symbol ?? '').toUpperCase();
  return {
    id: String(raw?.id ?? symbol ?? ''),
    symbol,
    name: raw?.name ?? symbol ?? 'Unknown',
    image: raw?.image ?? raw?.logo ?? undefined,
    price: num(raw?.currentPrice ?? raw?.price ?? raw?.current_price) ?? 0,
    change1h: num(raw?.priceChangePercentage1h ?? raw?.change1h),
    change24h: num(raw?.priceChangePercentage24h ?? raw?.change24h ?? raw?.price_change_percentage_24h) ?? 0,
    change7d: num(raw?.priceChangePercentage7d ?? raw?.change7d),
    marketCap: num(raw?.marketCap ?? raw?.market_cap) ?? 0,
    volume24h: num(raw?.totalVolume ?? raw?.volume24h ?? raw?.total_volume) ?? 0,
    rank: num(raw?.marketCapRank ?? raw?.market_cap_rank),
  };
}

function num(v: any): number | null {
  return typeof v === 'number' && isFinite(v) ? v : null;
}

// ---- Fetchers ------------------------------------------------------------------

/**
 * Token list. Prefers /market/tokens (real ranks + logos + 7d change); falls
 * back to /crypto/prices when the primary is down/rate-limited (429). Either
 * way the caller re-sorts via sortTokens(), so order is always correct.
 */
export async function fetchMarketTokens(): Promise<{ tokens: MarketToken[]; error?: string }> {
  const primary = await api<any>('/market/tokens?limit=50');
  if (primary.success) {
    const rows = Array.isArray(primary.data?.tokens)
      ? primary.data.tokens
      : Array.isArray(primary.data) ? primary.data : [];
    if (rows.length > 0) return { tokens: rows.map(normalizeToken) };
  }
  const fallback = await api<any>('/crypto/prices?limit=50');
  if (fallback.success) {
    const rows = Array.isArray(fallback.data) ? fallback.data : (fallback.data?.tokens ?? []);
    if (rows.length > 0) return { tokens: rows.map(normalizeToken) };
  }
  return { tokens: [], error: primary.error || fallback.error || 'Could not load market data.' };
}

export async function fetchMarketOverview(): Promise<MarketOverview> {
  const res = await api<any>('/crypto/market-overview');
  const d = res.success ? res.data : null;
  if (!d) return {};
  return {
    totalMarketCap: num(d.totalMarketCap ?? d.total_market_cap) ?? undefined,
    totalVolume24h: num(d.totalVolume24h ?? d.total_volume_24h) ?? undefined,
    btcDominance: num(d.btcDominance ?? d.btc_dominance) ?? undefined,
    ethDominance: num(d.ethDominance ?? d.eth_dominance) ?? undefined,
  };
}

// Whitepaper links for majors. The backend token-detail route maps CoinGecko
// `links` but does NOT expose a whitepaper URL, so the Coin Detail "White Paper"
// button uses this static, info-only map (viewing docs is v1-safe). TODO(data):
// drop this when the API forwards CoinGecko's links.whitepaper field.
const WHITEPAPERS: Record<string, string> = {
  BTC: 'https://bitcoin.org/bitcoin.pdf',
  ETH: 'https://ethereum.org/en/whitepaper/',
  SOL: 'https://solana.com/solana-whitepaper.pdf',
  BNB: 'https://www.bnbchain.org/en/blog',
  XRP: 'https://ripple.com/files/ripple_consensus_whitepaper.pdf',
  ADA: 'https://docs.cardano.org/about-cardano/introduction',
  AVAX: 'https://www.avalabs.org/whitepapers',
  DOT: 'https://polkadot.com/papers/Polkadot-whitepaper.pdf',
  MATIC: 'https://polygon.technology/papers/pol-whitepaper',
  POL: 'https://polygon.technology/papers/pol-whitepaper',
  LINK: 'https://research.chain.link/whitepaper-v2.pdf',
  UNI: 'https://uniswap.org/whitepaper-v3.pdf',
  AAVE: 'https://github.com/aave/aave-protocol/blob/master/docs/Aave_Protocol_Whitepaper_v1_0.pdf',
  LTC: 'https://litecoin.org',
  DOGE: 'https://github.com/dogecoin/dogecoin/blob/master/README.md',
  TRX: 'https://tron.network/static/doc/white_paper_v_2_0.pdf',
  ATOM: 'https://v1.cosmos.network/resources/whitepaper',
  XLM: 'https://stellar.org/learn/intro-to-stellar',
};

/** Detailed coin data for the Coin Detail screen (GET /market/tokens/:symbol). */
export async function fetchTokenDetail(symbol: string): Promise<TokenDetail | null> {
  const res = await api<any>(`/market/tokens/${encodeURIComponent(symbol.toLowerCase())}`);
  if (!res.success || !res.data) return null;
  const d = res.data;
  const md = d.marketData ?? {};
  const up = String(d.symbol ?? symbol).toUpperCase();
  // CoinGecko descriptions carry inline HTML anchors — strip tags for RN <Text>.
  const description = typeof d.description === 'string'
    ? d.description.replace(/<[^>]+>/g, '').trim()
    : undefined;
  return {
    id: String(d.id ?? up),
    symbol: up,
    name: d.name ?? up,
    image: d.image ?? undefined,
    description,
    categories: Array.isArray(d.categories) ? d.categories.filter(Boolean) : [],
    websiteUrl: d.links?.homepage || null,
    whitepaperUrl: d.links?.whitepaper || WHITEPAPERS[up] || null,
    twitterUrl: d.links?.twitter || null,
    price: num(md.currentPrice) ?? undefined,
    change24h: num(md.priceChangePercentage24h),
    change7d: num(md.priceChangePercentage7d),
    marketCap: num(md.marketCap) ?? undefined,
    volume24h: num(md.totalVolume) ?? undefined,
    rank: num(md.marketCapRank),
    high24h: num(md.high24h) ?? undefined,
    low24h: num(md.low24h) ?? undefined,
    circulatingSupply: num(md.circulatingSupply) ?? undefined,
    sparkline7d: Array.isArray(md.sparkline7d) ? md.sparkline7d.filter((n: any) => typeof n === 'number') : undefined,
  };
}

// ---- Chart ranges ----------------------------------------------------------------

// The design (IMG_5882) shows 1H … All Time range tabs. The backend history
// endpoint only serves tf = 24h | 7d | 30d | 1y, so 1H and ALL render as
// visible-but-disabled tabs until the API grows those ranges.
export interface ChartRange {
  label: string;
  tf: '24h' | '7d' | '30d' | '1y' | null; // null = not served by the API yet
}
export const CHART_RANGES: ChartRange[] = [
  { label: '1H', tf: null },
  { label: '1D', tf: '24h' },
  { label: '1W', tf: '7d' },
  { label: '1M', tf: '30d' },
  { label: '1Y', tf: '1y' },
  { label: 'All', tf: null },
];

/** Price history series for the chart. Returns [] when unavailable. */
export async function fetchTokenHistory(
  symbol: string,
  tf: NonNullable<ChartRange['tf']>
): Promise<{ time: string; price: number }[]> {
  const res = await api<any>(`/crypto/tokens/${encodeURIComponent(symbol.toUpperCase())}/history?tf=${tf}`);
  const rows = res.success && Array.isArray(res.data) ? res.data : [];
  return rows.filter((r: any) => typeof r?.price === 'number');
}

// ---- Sorting (the P0 fix) ---------------------------------------------------------

/**
 * Sort by the SELECTED METRIC — never by array order. "Market Cap" is true
 * market-cap descending (BTC first), Gainers/Losers by 24h %, Volume by 24h
 * volume. Rows missing the metric sink to the bottom instead of floating up.
 */
export function sortTokens(tokens: MarketToken[], key: SortKey): MarketToken[] {
  const list = Array.isArray(tokens) ? [...tokens] : [];
  switch (key) {
    case 'Gainers':
      return list.sort((a, b) => (b.change24h ?? -Infinity) - (a.change24h ?? -Infinity));
    case 'Losers':
      return list.sort((a, b) => (a.change24h ?? Infinity) - (b.change24h ?? Infinity));
    case 'Volume':
      return list.sort((a, b) => (b.volume24h || 0) - (a.volume24h || 0));
    case 'Market Cap':
    default:
      return list.sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0));
  }
}

// ---- Row sparkline series -----------------------------------------------------------

/**
 * Per-row mini series for the markets list (IMG_5879). /market/tokens is fetched
 * with sparkline:false server-side, so no real intraweek series exists per row.
 * We build an HONEST interpolation through the REAL data points we do have —
 * the price 7d ago / 24h ago / 1h ago (derived from the % changes) and now.
 * No random noise is added: the line's shape is exactly the real trend anchors.
 *
 * TODO(data): switch to the true 7d sparkline once the API requests
 * sparkline=true on /market/tokens (routes/market.ts currently disables it).
 */
export function approxSeries(t: MarketToken, points = 24): number[] {
  const p = t.price || 0;
  if (!p) return [0, 0];
  const at = (pct?: number | null) => (pct == null ? null : p / (1 + pct / 100));
  // [hoursAgo, price] anchors from real % changes, oldest first.
  const anchors: Array<[number, number]> = [];
  const p7 = at(t.change7d); if (p7 != null) anchors.push([168, p7]);
  const p24 = at(t.change24h); if (p24 != null) anchors.push([24, p24]);
  const p1 = at(t.change1h); if (p1 != null) anchors.push([1, p1]);
  anchors.push([0, p]);
  if (anchors.length < 2) return [p, p]; // no % data → honest flat line

  const span = anchors[0][0];
  const out: number[] = [];
  for (let i = 0; i < points; i++) {
    const h = span * (1 - i / (points - 1)); // hoursAgo, decreasing to 0
    let j = 0;
    while (j < anchors.length - 2 && h < anchors[j + 1][0]) j++;
    const [h0, v0] = anchors[j];
    const [h1, v1] = anchors[j + 1];
    const f = h0 === h1 ? 1 : (h0 - h) / (h0 - h1);
    out.push(v0 + (v1 - v0) * Math.min(1, Math.max(0, f)));
  }
  return out;
}

// ---- Formatters -----------------------------------------------------------------------

export function formatPrice(n?: number | null): string {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1) return '$' + n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
  if (n === 0) return '$0.00';
  return '$' + n.toPrecision(4);
}

export function formatLargeNumber(n?: number | null): string {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1e12) return '$' + (n / 1e12).toFixed(2) + 'T';
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(2) + 'B';
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M';
  return '$' + Math.round(n).toLocaleString();
}

export function formatSupply(n?: number | null, symbol?: string): string {
  if (n == null || isNaN(n)) return '—';
  const s = n >= 1e9 ? (n / 1e9).toFixed(2) + 'B' : n >= 1e6 ? (n / 1e6).toFixed(2) + 'M' : Math.round(n).toLocaleString();
  return symbol ? `${s} ${symbol}` : s;
}

// ---- Community matching ------------------------------------------------------------------

/**
 * Find the CRYB community for a coin (Coin Detail "Join Community" CTA).
 * Matches community name/displayName against the coin's name or symbol
 * (e.g. "Bitcoin Maxis" ⇒ BTC/Bitcoin). Returns null when nothing matches —
 * the CTA hides rather than dead-ending.
 */
export function matchCommunityForCoin(
  communities: any[],
  symbol: string,
  name: string
): any | null {
  if (!Array.isArray(communities)) return null;
  const sym = (symbol || '').trim().toLowerCase();
  const coin = (name || '').trim().toLowerCase();
  if (!sym && !coin) return null;
  const score = (c: any): number => {
    const hay = `${c?.name ?? ''} ${c?.displayName ?? ''} ${c?.description ?? ''}`.toLowerCase();
    const title = `${c?.name ?? ''} ${c?.displayName ?? ''}`.toLowerCase();
    if (coin && title.includes(coin)) return 3;         // "Bitcoin Maxis"
    if (sym && new RegExp(`\\b${sym}\\b`).test(title)) return 2; // "BTC Traders"
    if (coin && hay.includes(coin)) return 1;           // mention in description
    return 0;
  };
  let best: any = null;
  let bestScore = 0;
  for (const c of communities) {
    const s = score(c);
    if (s > bestScore) { best = c; bestScore = s; }
  }
  return bestScore > 0 ? best : null;
}
